import { Chart, ChartConfiguration } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Register datalabels globally; charts that don't need it must explicitly set display:false
Chart.register(ChartDataLabels);

/** Bar count above which the chart becomes horizontally scrollable. */
const SCROLL_THRESHOLD = 10;
/** Minimum pixel width per bar slot when horizontal scroll is active. */
const BAR_MIN_WIDTH_PX = 30;

export async function initCharts(parentElem: HTMLElement | Document): Promise<void> {
  const charts = parentElem.querySelectorAll<HTMLCanvasElement>('.js-chart');
  charts.forEach((canvas) => {
    const dataAttr = canvas.dataset.chartData;
    if (!dataAttr) return;

    // Destroy any existing instance so re-renders don't leak chart objects
    Chart.getChart(canvas)?.destroy();

    const config: ChartConfiguration = JSON.parse(dataAttr);

    enrichBarChartWithFormatting(config);

    // Apply scroll wrapper after config enrichment so bar count is accurate
    const barCount = getBarCount(config);
    applyScrollWrapper(canvas, barCount);

    new Chart(canvas, config);
  });
}

/** Returns the number of bars in a bar chart config, 0 for other chart types. */
function getBarCount(config: ChartConfiguration): number {
  if (config.type !== 'bar') return 0;
  return (config.data?.labels?.length ?? config.data?.datasets?.[0]?.data?.length ?? 0) as number;
}

/**
 * Wraps the canvas in a horizontally-scrollable container when the number of
 * bars exceeds SCROLL_THRESHOLD. Chart.js responsive mode sizes the canvas to
 * the inner "sizer" div (which enforces a min-width), and the outer scroller
 * provides the scrollable viewport — so the rest of the layout is unaffected.
 *
 * This is safe to call on every initCharts invocation because the AJAX reload
 * in dashboard.ts replaces container.innerHTML before calling initCharts again,
 * giving us a fresh canvas with no leftover wrappers.
 */
function applyScrollWrapper(canvas: HTMLCanvasElement, barCount: number): void {
  if (barCount <= SCROLL_THRESHOLD) return;

  const minWidth = barCount * BAR_MIN_WIDTH_PX;

  // Resolve the effective height of the parent container.
  // `height: 100%` on children doesn't resolve when the parent only has `min-height`
  // (no explicit `height`), which causes Chart.js to fall back to its 300 px default.
  // Reading the computed height gives us the actual rendered size, which includes
  // the min-height contribution, so we can set a concrete pixel height on the scroller.
  const parent = canvas.parentElement;
  const resolvedHeight = parent ? parseFloat(getComputedStyle(parent).height) : 0;
  const heightCss = resolvedHeight > 0 ? `${resolvedHeight}px` : '100%';

  // Inner div: sized wide enough for all bars; Chart.js sizes the canvas to it.
  const sizer = document.createElement('div');
  sizer.style.cssText = `min-width: ${minWidth}px; height: 100%; position: relative;`;

  // Outer div: scrollable viewport at full container width.
  const scroller = document.createElement('div');
  scroller.className = 'chart-scroll-wrapper';
  scroller.style.cssText = `overflow-x: auto; width: 100%; height: ${heightCss}; -webkit-overflow-scrolling: touch;`;

  canvas.parentNode?.insertBefore(scroller, canvas);
  scroller.appendChild(sizer);
  sizer.appendChild(canvas);
}

/**
 * Returns true when the viewport matches a mobile screen (Bootstrap < md, < 768 px).
 * Evaluated at chart render time so AJAX re-renders always reflect the current viewport.
 */
function isMobileScreen(): boolean {
  return window.matchMedia('(max-width: 767.98px)').matches;
}

/**
 * Enriches a bar chart config with:
 *  - Smooth load animation (overrides the server-side animation: false default)
 *  - Currency datalabels above bars (hidden on mobile screens)
 *  - Currency-formatted tooltip labels
 *  - Currency-formatted Y-axis ticks
 * For all other chart types, datalabels are disabled to keep visuals clean.
 */
function enrichBarChartWithFormatting(config: ChartConfiguration): void {
  config.options ??= {};
  config.options.plugins ??= {};

  if (config.type !== 'bar') {
    (config.options.plugins as Record<string, unknown>).datalabels = { display: false };
    return;
  }

  const datasets = (config.data?.datasets ?? []) as unknown as Array<Record<string, unknown>>;
  const formattedDataset = datasets.find(
    (ds) => Array.isArray(ds['formattedValues']) && (ds['formattedValues'] as unknown[]).length > 0
  );

  if (!formattedDataset) {
    (config.options.plugins as Record<string, unknown>).datalabels = { display: false };
    return;
  }

  const locale: string = 'cs-CZ';

  // Extra top padding when datalabels are visible (desktop only) so they aren't clipped
  config.options.layout = { padding: { top: isMobileScreen() ? 8 : 28 } };

  // ── Datalabels: show formatted price above each bar ───────────────────────
  // Hidden on mobile screens (< 768 px) where there isn't enough space.
  (config.options.plugins as Record<string, unknown>).datalabels = {
    anchor: 'end',
    align: 'end',
    offset: 2,
    display: () => !isMobileScreen(),
    formatter: (_value: unknown, ctx: Record<string, unknown>) => {
      const ds = ctx['dataset'] as Record<string, unknown>;
      const idx = ctx['dataIndex'] as number;
      return (ds['formattedValues'] as string[])?.[idx] ?? '';
    },
    font: { size: 11, weight: 'bold' },
    color: '#495057',
    clip: false,
  };

  // ── Tooltip: replace raw number with formatted currency string ────────────
  const existingTooltip =
    ((config.options.plugins as Record<string, unknown>)['tooltip'] as Record<string, unknown>) ??
    {};
  (config.options.plugins as Record<string, unknown>).tooltip = {
    ...existingTooltip,
    callbacks: {
      label: (item: Record<string, unknown>) => {
        const ds = item['dataset'] as Record<string, unknown>;
        const idx = item['dataIndex'] as number;
        return (ds['formattedValues'] as string[])?.[idx] ?? (item['formattedValue'] as string);
      },
    },
  };

  // ── Y-axis ticks: abbreviated currency (e.g. "50 000 Kč") ────────────────
  config.options.scales ??= {};
  const scales = config.options.scales as Record<string, Record<string, unknown>>;
  scales['y'] ??= {};
  scales['y']['ticks'] = {
    ...(scales['y']['ticks'] as Record<string, unknown>),
    callback: buildCurrencyTickFormatter(locale),
  };
}

/** Returns an Intl tick formatter for the given locale, using currency style when possible. */
function buildCurrencyTickFormatter(locale: string): (value: number | string) => string {
  const currencyByLocale: Record<string, string> = {
    'cs-CZ': 'CZK',
    'de-DE': 'EUR',
  };
  const currency = currencyByLocale[locale];
  const fmt = currency
    ? new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 })
    : new Intl.NumberFormat(locale, { style: 'decimal', maximumFractionDigits: 0 });

  return (value: number | string) =>
    typeof value === 'number' ? fmt.format(value) : String(value);
}
